/**
 * Single-case detail hooks (PAC-010, PAC-023).
 *
 * Wraps the Cases service case-page surface:
 *   - GET  /api/v1/cases/{id}            → Case (full case page)
 *   - GET  /api/v1/cases/{id}/timeline   → CaseEvent[] (append-only timeline)
 *   - POST /api/v1/cases/{id}/attempts   → log a structured contact attempt
 *   - POST /api/v1/cases/{id}/consent    → capture TCPA/HIPAA/e-sign consent
 *
 * The case + timeline load together; the two write actions append a timeline
 * event server-side, so each refreshes the timeline on success.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { Case, CaseAttempt, CaseEvent, ConsentRecord } from '../api/types';

/** Inbound payload to log a structured contact attempt. */
export interface LogAttemptBody {
  contact_method: string;
  outcome_code: string;
  notes_structured?: Record<string, unknown>;
}

/** Inbound payload to capture consent. */
export interface CaptureConsentBody {
  consent_type: string;
  channel: string;
  version: string;
}

interface UseCaseResult {
  caseRecord: Case | null;
  timeline: CaseEvent[];
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  refreshTimeline: () => Promise<void>;
  logAttempt: (body: LogAttemptBody) => Promise<CaseAttempt | null>;
  captureConsent: (body: CaptureConsentBody) => Promise<ConsentRecord | null>;
}

/** Loads a case + its timeline and exposes the attempt/consent write actions. */
export function useCase(caseId: string | undefined): UseCaseResult {
  const [caseRecord, setCaseRecord] = useState<Case | null>(null);
  const [timeline, setTimeline] = useState<CaseEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refreshTimeline = useCallback(async () => {
    if (!caseId) return;
    try {
      const events = await apiClient.get<CaseEvent[]>(
        `/cases/${encodeURIComponent(caseId)}/timeline`
      );
      setTimeline(events ?? []);
    } catch {
      // Timeline is supplementary; leave any prior value in place on error.
    }
  }, [caseId]);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    Promise.all([
      apiClient.get<Case>(`/cases/${encodeURIComponent(caseId)}`),
      apiClient
        .get<CaseEvent[]>(`/cases/${encodeURIComponent(caseId)}/timeline`)
        .catch(() => [] as CaseEvent[]),
    ])
      .then(([record, events]) => {
        setCaseRecord(record);
        setTimeline(events ?? []);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setCaseRecord(null);
        setTimeline([]);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logAttempt = useCallback(
    async (body: LogAttemptBody): Promise<CaseAttempt | null> => {
      if (!caseId) return null;
      setIsWorking(true);
      setActionError(null);
      try {
        const created = await apiClient.post<CaseAttempt>(
          `/cases/${encodeURIComponent(caseId)}/attempts`,
          body
        );
        await refreshTimeline();
        return created;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    [caseId, refreshTimeline]
  );

  const captureConsent = useCallback(
    async (body: CaptureConsentBody): Promise<ConsentRecord | null> => {
      if (!caseId) return null;
      setIsWorking(true);
      setActionError(null);
      try {
        const created = await apiClient.post<ConsentRecord>(
          `/cases/${encodeURIComponent(caseId)}/consent`,
          body
        );
        await refreshTimeline();
        return created;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    [caseId, refreshTimeline]
  );

  return {
    caseRecord,
    timeline,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    refreshTimeline,
    logAttempt,
    captureConsent,
  };
}
